import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyCall from "@/components/StickyCall";
import BookingForm from "@/components/BookingForm";
import Seo, { LOCAL_BUSINESS_SCHEMA } from "@/components/Seo";
import { CITIES, COMPANY, SERVICES } from "@/lib/data";
import { ArrowRight, Phone, MapPin, ShieldCheck, Clock3, Heart, CheckCircle2 } from "lucide-react";

const CITY_INTRO = (name) =>
  `Comfort Care Transportation is ${name}'s trusted provider of non-emergency medical transportation. We deliver safe, reliable, ADA-compliant rides for seniors, wheelchair users, dialysis patients, hospital discharges, and anyone who needs dependable transportation to a medical appointment. Our trained drivers, wheelchair-accessible vehicles, and door-through-door service give families across ${name} peace of mind every time we drive.`;

const WHY_CITY = (name) => [
  {
    title: `Local ${name} expertise`,
    body: `Our drivers know ${name} streets, traffic patterns, and major medical facilities — so your loved one arrives on time, every time.`,
  },
  {
    title: "ADA-compliant wheelchair vans",
    body: `Properly equipped vehicles with lifts, ramps, and securement systems for safe transport in ${name} and surrounding areas.`,
  },
  {
    title: "Trained, compassionate drivers",
    body: `${name} families trust our team because we lead with patience, dignity, and respect — for every passenger, every ride.`,
  },
  {
    title: "Recurring & one-time schedules",
    body: `Whether it's a single hospital discharge or recurring dialysis runs, we tailor a schedule that fits ${name} appointment needs.`,
  },
];

const KEYWORDS = (name) => [
  `${name} medical transportation`,
  `${name} wheelchair transportation`,
  `NEMT ${name} VA`,
  `dialysis transportation ${name}`,
  `senior transportation ${name}`,
  `hospital discharge ride ${name}`,
];

export default function CityPage() {
  const { city } = useParams();
  const data = CITIES.find((c) => c.slug === city);

  useEffect(() => {
    if (data) {
      window.scrollTo(0, 0);
    }
  }, [data]);

  if (!data) return <Navigate to="/" replace />;

  const stateLabel = data.region === "Maryland" ? "MD" : data.region === "District of Columbia" ? "DC" : "VA";
  const fullLocation = data.region === "District of Columbia" ? "Washington DC" : `${data.name}, ${stateLabel}`;

  const seoTitle = `${data.name} Medical Transportation | NEMT ${stateLabel} | Comfort Care 571-689-4470`;
  const seoDescription = `Non-emergency medical transportation in ${fullLocation}. Wheelchair accessible vans, dialysis & hospital discharge rides, senior NEMT. Trusted local NEMT serving ${data.region}. Call 571-689-4470.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      LOCAL_BUSINESS_SCHEMA,
      {
        "@type": "Service",
        "@id": `https://comfortcarenemt.com/service-area/${data.slug}#service`,
        serviceType: "Non-Emergency Medical Transportation",
        name: `${data.name} Medical Transportation`,
        description: `ADA-compliant non-emergency medical transportation in ${fullLocation}. Wheelchair, ambulatory, dialysis, hospital discharge, and senior rides.`,
        provider: { "@id": "https://comfortcarenemt.com/#business" },
        areaServed: {
          "@type": "City",
          name: data.name,
          containedInPlace: {
            "@type": data.region === "Maryland" ? "State" : data.region === "District of Columbia" ? "AdministrativeArea" : "State",
            name: data.region === "Maryland" ? "Maryland" : data.region === "District of Columbia" ? "Washington, D.C." : "Virginia",
          },
        },
        offers: {
          "@type": "Offer",
          availability: "https://schema.org/InStock",
          priceCurrency: "USD",
          areaServed: data.name,
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://comfortcarenemt.com/" },
          { "@type": "ListItem", position: 2, name: "Service Area", item: "https://comfortcarenemt.com/#service-area" },
          { "@type": "ListItem", position: 3, name: data.name, item: `https://comfortcarenemt.com/service-area/${data.slug}` },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={`/service-area/${data.slug}`}
        image="https://customer-assets.emergentagent.com/job_comfort-care-booking/artifacts/l5bnk78a_2C3E0AE7-BC6B-41D5-8937-5C064B8E8692.PNG"
        keywords={[
          `${data.name} medical transportation`,
          `${data.name} NEMT`,
          `${data.name} wheelchair transportation`,
          `${data.name} dialysis transportation`,
          `${data.name} hospital discharge ride`,
          `${data.name} senior transportation`,
          `${data.name} non emergency medical transport`,
          `medical transportation ${stateLabel}`,
          `wheelchair van ${data.name}`,
        ]}
        jsonLd={jsonLd}
      />
      <Header />
      <main className="pt-24">
        {/* Hero */}
        <section className="relative grain-bg py-16 md:py-24">
          <div className="container-pad mx-auto max-w-7xl">
            <Link to="/" className="text-sm text-navy/60 hover:text-teal-600">← Back to Home</Link>
            <div className="grid lg:grid-cols-12 gap-10 mt-6 items-center">
              <div className="lg:col-span-7">
                <span className="eyebrow">
                  <MapPin className="w-3.5 h-3.5" /> {data.region} · Virginia
                </span>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-navy tracking-tight mt-5 leading-[1.05]">
                  {data.name} <strong className="text-teal-600 font-bold">Medical Transportation</strong>
                </h1>
                <p className="mt-6 text-lg text-navy/70 leading-relaxed max-w-2xl font-light">{data.blurb}</p>
                <div className="flex flex-wrap gap-3 mt-7">
                  <a href="#book" className="btn-primary">Request a Ride <ArrowRight className="w-4 h-4" /></a>
                  <a href={`tel:${COMPANY.phoneRaw}`} className="btn-outline">
                    <Phone className="w-4 h-4" /> Call {COMPANY.phone}
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="relative">
                  <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-teal-500/10 blur-2xl" />
                  <img
                    src="https://customer-assets.emergentagent.com/job_comfort-care-booking/artifacts/l28afqe7_Screenshot%202026-06-05%20at%209.16.56%E2%80%AFAM.png"
                    alt={`${data.name} wheelchair accessible transportation`}
                    className="rounded-3xl w-full h-[380px] object-cover shadow-xl relative"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Intro / SEO content */}
        <section className="section-pad">
          <div className="container-pad mx-auto max-w-4xl">
            <h2 className="font-heading text-3xl md:text-4xl font-light text-navy leading-tight">
              Non-emergency medical transportation, designed for <strong className="font-bold text-teal-600">{data.name}</strong>.
            </h2>
            <p className="mt-6 text-navy/75 text-lg leading-relaxed font-light">{CITY_INTRO(data.name)}</p>

            <h3 className="font-heading text-2xl font-semibold text-navy mt-12 mb-4">Who we transport in {data.name}</h3>
            <ul className="space-y-3">
              {[
                `Wheelchair users and non-ambulatory passengers in ${data.name}`,
                `Seniors traveling to and from doctor appointments`,
                `Dialysis patients on recurring treatment schedules`,
                `Patients being discharged from ${data.hospitals?.[0] || "local hospitals"}`,
                `Physical therapy and rehabilitation appointments`,
                `Assisted living and nursing home residents`,
                `Adult day program participants`,
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-teal-500 mt-0.5 shrink-0" />
                  <span className="text-navy/80">{line}</span>
                </li>
              ))}
            </ul>

            <h3 className="font-heading text-2xl font-semibold text-navy mt-12 mb-4">Major {data.name} facilities we serve</h3>
            <p className="text-navy/75 leading-relaxed">
              We coordinate medical rides to {data.hospitals?.join(", ") || "all major medical facilities"}, specialty clinics, dialysis centers,
              rehab facilities, and primary care providers throughout {data.name} and the surrounding {data.region}. Our familiarity with
              the area means precise, timely arrivals — every appointment, every time.
            </p>

            <h3 className="font-heading text-2xl font-semibold text-navy mt-12 mb-6">Why families in {data.name} choose Comfort Care</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              {WHY_CITY(data.name).map((w) => (
                <div key={w.title} className="card-soft p-6">
                  <h4 className="font-heading font-semibold text-navy">{w.title}</h4>
                  <p className="text-navy/65 text-sm mt-2 leading-relaxed">{w.body}</p>
                </div>
              ))}
            </div>

            <h3 className="font-heading text-2xl font-semibold text-navy mt-12 mb-4">Services available in {data.name}</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {SERVICES.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-100 hover:border-teal-300 hover:bg-sand transition group">
                  <Heart className="w-4 h-4 text-teal-500 shrink-0" />
                  <span className="text-navy/85 font-medium group-hover:text-teal-700 transition">{s.title}</span>
                </Link>
              ))}
            </div>

            <div className="mt-12 bg-sand rounded-3xl p-8 border border-sand-200">
              <h3 className="font-heading text-2xl font-semibold text-navy">Search terms we serve in {data.name}</h3>
              <div className="flex flex-wrap gap-2 mt-5">
                {KEYWORDS(data.name).map((k) => (
                  <span key={k} className="px-4 py-1.5 rounded-full bg-white border border-gray-100 text-sm text-navy/70 capitalize">{k}</span>
                ))}
              </div>
              <p className="text-navy/70 mt-6 leading-relaxed">
                If you searched for medical transportation, wheelchair vans, dialysis transport, or senior rides in {data.name},
                you've found the right team. Our schedulers are available 24/7 to help you plan trips that work with your appointment calendar.
              </p>
            </div>

            <h3 className="font-heading text-2xl font-semibold text-navy mt-12 mb-4">Booking a {data.name} medical ride</h3>
            <p className="text-navy/75 leading-relaxed">
              Booking is straightforward. Submit a ride request below or call {COMPANY.phone}. Our team reviews your trip,
              confirms pricing and vehicle assignment, and locks in a pickup window that meets your appointment time. For recurring
              treatment schedules — like dialysis or rehab — we'll build a long-term plan so you never have to think about
              transportation logistics again.
            </p>

            <p className="text-navy/75 leading-relaxed mt-4">
              Every passenger in {data.name} receives door-through-door service, a clean and properly equipped vehicle, and a driver
              trained in safe transfers, securement, and patient sensitivity. We treat your loved one the way we'd want our own family treated.
            </p>
          </div>
        </section>

        {/* Booking */}
        <section id="book" className="section-pad bg-sand">
          <div className="container-pad mx-auto max-w-6xl">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <h2 className="font-heading text-3xl md:text-4xl font-light text-navy leading-tight">
                Request your {data.name} ride
              </h2>
              <p className="text-navy/65 mt-3">Our team will confirm pricing, vehicle, and pickup time.</p>
            </div>
            <BookingForm />
          </div>
        </section>

        {/* Trust strip */}
        <section className="bg-navy text-white py-14">
          <div className="container-pad mx-auto max-w-7xl grid sm:grid-cols-3 gap-8 text-center">
            <Stat icon={ShieldCheck} title="ADA Compliant" body="Certified safety and accessibility standards." />
            <Stat icon={Heart} title="Compassionate Care" body="Dignity-first service for every passenger." />
            <Stat icon={Clock3} title="On-Time, Every Time" body="Reliable pickups and confirmed scheduling." />
          </div>
        </section>
      </main>
      <Footer />
      <StickyCall />
    </div>
  );
}

function Stat({ icon: Icon, title, body }) {
  return (
    <div>
      <Icon className="w-7 h-7 text-teal-300 mx-auto" />
      <div className="font-heading font-semibold text-white mt-3">{title}</div>
      <div className="text-white/60 text-sm mt-1">{body}</div>
    </div>
  );
}
